type PerformanceValue = string | number;

interface Performance {
    serverResponse: PerformanceValue;
    domComplete: PerformanceValue;
    domInteractive: PerformanceValue;
    firstContentfulPaint: PerformanceValue;
    largestContentfulPaint: PerformanceValue;
}

/**
 * Класс для работы с PerformanceObserver API
 * собираем с разных событий все данные в один объект и возвращаем по заключению загрузки
 * возвращает fulfilled промиса который мы создаем методом run()вместе с объектом Performance
 *
 * @todo добавить:
 * - Cumulative Layout Shift (CLS) https://web.dev/articles/cls?hl=ru
 * - Interaction to Next Paint (INP) https://web.dev/articles/inp?hl=ru
 * Тут из сложностей нужно видимо вешать ограничение время работы метода run()
 * т.к. пльзователь может не нажать кнопку и не закрыть страницу никогда
 *
 */

export class PerformanceObserverAdapter {
    public performance: Performance;
    private timeout: number;

    constructor(timeout: number = 15000) {
        this.performance = {
            serverResponse: 0,
            domComplete: 0,
            domInteractive: 0,
            firstContentfulPaint: 0,
            largestContentfulPaint: 0,
        };
        this.timeout = timeout;
    }

    private async navigationObserver(): Promise<Performance> {
        return new Promise((resolve) => {
            const observer = new PerformanceObserver((list) => {
                const entry = list.getEntries()[0] as PerformanceNavigationTiming;
                this.processNavigationEntry(entry);
            });
    
            observer.observe({ type: 'navigation', buffered: true });
    
            let elapsedTime = 0;
            const interval = this.timeout / 3;

            const checkDomComplete = setInterval(() => {
                elapsedTime += interval;

                if (this.performance.domComplete && this.performance.domComplete !== '0.000') {
                    clearInterval(checkDomComplete);
                    resolve(this.performance);
                } else if (elapsedTime >= this.timeout) {
                    clearInterval(checkDomComplete);
                    resolve(this.performance);
                }
            }, interval);
        });
    }

    private processNavigationEntry(entry: PerformanceNavigationTiming) {
        if (entry.responseStart) {
            this.setPerformanceValue('serverResponse', this.millisToSecondsString(entry.responseStart));
        }
        if (entry.domInteractive) {
            this.setPerformanceValue('domInteractive', this.millisToSecondsString(entry.domInteractive));
        }
        if (entry.domComplete) {
            this.setPerformanceValue('domComplete', this.millisToSecondsString(entry.domComplete));
        }
    }

    /**
     * Первая отрисовка контента (FCP)
     *
     * Отмечает первую точку на временной шкале загрузки страницы,
     * когда пользователь может видеть что-либо на экране
     * @docs https://web.dev/articles/fcp?hl=ru
     */
    private async firstContentfulPaintObserver(): Promise<void> {
        return new Promise((resolve) => {
            new PerformanceObserver((entryList) => {
                for (const entry of entryList.getEntriesByName(
                    'first-contentful-paint'
                )) {
                    this.setPerformanceValue(
                        'firstContentfulPaint',
                        this.millisToSecondsString(entry.startTime)
                    );
                }

                resolve();
            }).observe({ type: 'paint', buffered: true });
        });
    }

    /**
     * Наибольшая отрисовка контента (LCP)
     *
     * отмечает точку на временной шкале загрузки страницы,
     * когда, скорее всего, загрузился основной контент страницы
     * @docs https://web.dev/articles/lcp?hl=ru#differences-metric-api
     */
    private async largestContentfulPaintObserver(): Promise<void> {
        return new Promise((resolve) => {
            new PerformanceObserver((entryList) => {
                for (const entry of entryList.getEntries()) {
                    this.setPerformanceValue(
                        'largestContentfulPaint',
                        this.millisToSecondsString(entry.startTime)
                    );
                }

                resolve();
            }).observe({ type: 'largest-contentful-paint', buffered: true });
        });
    }

    private setPerformanceValue(key: string, value: PerformanceValue) {
        this.performance[key] = value;
    }

    private millisToSecondsString(millis: number): string {
        return (millis / 1000).toFixed(3);
    }

    public run(): Promise<Performance> {
        /* eslint-disable no-async-promise-executor */
        const timeoutPromise = new Promise<Performance>((resolve) => {
            setTimeout(() => resolve(this.performance), this.timeout);
        });

        // Запускаем все наблюдатели параллельно
        const observersPromise = Promise.all([
            this.navigationObserver(),
            this.firstContentfulPaintObserver(),
            this.largestContentfulPaintObserver()
        ]).then(() => {
            return this.performance
        });

        // Возвращаем первый завершившийся промис (или таймаут)
        return Promise.race([observersPromise, timeoutPromise]);
    }
}
